import React from 'react';
import {List, Map} from 'immutable';
import {browserHistory} from 'react-router';
import AuthStore from 'stores/AuthStore';
import AdminDashboardSidebar from 'components/dashboard/admin/AdminDashboardSidebar';
import ManagerDashboardSidebar from 'components/dashboard/ManagerDashboardSidebar';
import CoachDashboardSidebar from 'components/dashboard/CoachDashboardSidebar';
import DashboardHeader from 'components/dashboard/DashboardHeader';
import connectToStores from 'alt-utils/lib/connectToStores';
import UserStore from 'stores/UserStore';
import UserActions from 'actions/UserActions';
import './StatsPage2.styl';
import buildUrl from 'components/dashboard/buildUrl';
import TeamListStore from 'stores/TeamListStore';
import UpcomingCoachingAgenda, {
  loadUpcomingReviewData,
} from 'components/dashboard/UpcomingCoachingAgenda';
import DirectReportsStore from 'stores/coaching/DirectReportsStore';
import FolderStore from 'stores/inbox/FolderStore';
import TagStore from 'stores/TagStore';
import {associateBy} from 'utils/associateBy';
import FolderActions from 'actions/inbox/FolderActions';
import DirectReportsActions from 'actions/coaching/DirectReportsActions';
import TagActions from 'actions/TagActions';
import getFilters from 'components/inbox/utils/getFilters';
import ManagerDashboardActions from 'actions/ManagerDashboardActions';
import DashboardHistoryActions from 'actions/dashboard/DashboardHistoryActions';
import DashboardHistoryStore from 'stores/dashboard/DashboardHistoryStore';
import DashboardBackButton from 'components/dashboard/DashboardBackButton';
import AssumedUserBadge from 'components/dashboard/AssumedUserBadge';
import ManagerDashboardStore from 'stores/ManagerDashboardStore';
import UserSource from 'sources/UserSource';
import {getCoachRepLink} from 'components/dashboard/coach/CoachSummaryDashboard';
import withCoachingPlanWizard from 'components/coaching/wizard/withCoachingPlanWizard';
import SkillActions from 'actions/SkillActions';
import UpcomingReviewsActions from 'actions/coaching/UpcomingReviewsActions';
import ModalExtrasContainer from 'components/dashboard/common/ModalExtrasContainer';
import ExecDashboardActions from 'actions/ExecDashboardActions';
import DepartmentFilterActions from 'actions/DepartmentFilterActions';
import DepartmentFilterStore from 'stores/DepartmentFilterStore';
import hasFlag from 'utils/hasFlag';
import {featureFlags, SortDirection} from 'utils/const';
import CoachingReviewTypeStore from 'stores/coaching/CoachingReviewTypeStore';
import AdminDashSearchSidebar from 'components/dashboard/admin/AdminDashSearchSidebar';
import {getNumberQueryParam, getQueryParam, getStringQueryParam} from 'utils/getQueryParam';
import KpiObservationStore from '@store/kpi/KpiObservationStore';
import KpiObservationActions from '@store/kpi/KpiObservationActions';
import DashboardDateRangeStore, {
  dateRangeForDashboard,
  dateRangeForHeader,
} from 'stores/dashboard/DashboardDateRangeStore';
import {
  DashboardTypeEnum,
  DashUserParams,
  ensureReviewMetadata,
  getDashboardType,
  HISTORY_ACTIONS,
  shouldRenderSidebar,
} from 'components/StatsPage2.utils';
import UPCOMING_REVIEW_ROLES from 'actions/coaching/UPCOMING_REVIEW_ROLES';
import {refreshCoachingPlansAndVelocities} from 'components/coaching/landing-pages/RefreshCoachingPlansAndVelocities';
import Team from 'records/Team';
import {Int} from '@store/@types/util';
import {RouterLocation} from '@store/@types/util/ReactRouter';
import {WithCoachingPlanWizardProps} from 'components/coaching/wizard/withCoachingPlanWizardProps';
import fetchMissingUsers from 'utils/fetchMissingUsers';
import TeamActions from 'actions/TeamActions';

interface StatsPage2Props {
  location: RouterLocation;
  params: DashUserParams;
}

interface StatsPage2State {
  selectedTeam: Team | null;
  coachingPlansTracker: Int;
}

/**
 * Entry point for Dashboard, fetches data used by all child components
 * Route params are defined as
 * /admin/<adminId>/manager/<managerId>/coach/<coachId>/<page-name>
 */
@connectToStores
class StatsPage2 extends React.Component<
  StatsPage2Props & ReturnType<typeof StatsPage2.getPropsFromStores> & WithCoachingPlanWizardProps,
  StatsPage2State
> {
  constructor(props) {
    super(props);
    this.state = {
      selectedTeam: null,
      coachingPlansTracker: 0,
    };
    this.onSelectTeam = this.onSelectTeam.bind(this);
    this.redirectToSamePageType = this.redirectToSamePageType.bind(this);
    this.refreshCoachingData = this.refreshCoachingData.bind(this);
    this.maybePrepopCoachingPlanDialog = this.maybePrepopCoachingPlanDialog.bind(this);
  }

  static getStores() {
    return [
      CoachingReviewTypeStore,
      DashboardDateRangeStore,
      DashboardHistoryStore,
      DepartmentFilterStore,
      DirectReportsStore,
      FolderStore,
      KpiObservationStore,
      ManagerDashboardStore,
      TagStore,
      UserStore,
    ];
  }

  static getPropsFromStores({params = {}}: StatsPage2Props) {
    const directReports = DirectReportsStore.getState().directReports;
    const folders = FolderStore.getState().folders;
    const tags = TagStore.getState().tags;
    const users = UserStore.getState().users;
    const sessionUser = AuthStore.getState().user;
    const {kpiObservationsByUser, kpiRequestsByUser} = KpiObservationStore.getState();

    const coach = users.get(params.coachId) ?? null;
    const manager = users.get(params.managerId) ?? null;
    const admin = users.get(params.adminId) ?? null;
    const rep = users.get(params.repId) ?? null;

    const inbox = folders.find(f => f.name.toLowerCase() === 'inbox');
    const tagIdsByName = {};
    if (tags) {
      tags.forEach(tag => {
        tagIdsByName[tag.name] = tag.id;
      });
    }

    // Does this user have active or finished kpis?
    const targetUserId = params.coachId || params.managerId || params.adminId;
    const showKpiTab =
      !kpiRequestsByUser.get(targetUserId, List()).isEmpty() ||
      !kpiObservationsByUser.get(targetUserId, List()).isEmpty();

    const {startDate, endDate} = dateRangeForDashboard(DashboardDateRangeStore.getState());
    const {startDate: headerStartDate, endDate: headerEndDate} = dateRangeForHeader(
      DashboardDateRangeStore.getState()
    );
    const {isTopicRollup, topicRollupDateRanges} = DashboardDateRangeStore.getState();

    // null out undefined values
    // TODO check if null specific checks are every done
    // can we just use undefined here?
    const {adminId = null, managerId = null, coachId = null} = params;
    return {
      adminId,
      managerId,
      coachId,
      sessionUser,
      manager,
      coach,
      admin,
      rep,
      directReports: associateBy(directReports, it => it.id),
      inboxId: inbox?.id ?? 0,
      tagIdsByName: Map(tagIdsByName),
      hasRenderedDashv2Gif: ManagerDashboardStore.getState().hasRenderedDashv2Gif,
      selectedDepartments: DepartmentFilterStore.getState().departmentSubset,
      showKpiTab,
      startDate,
      endDate,
      headerStartDate,
      headerEndDate,
      topicRollupDateRanges,
      isTopicRollup,
    };
  }

  fetchKpiData() {
    const userBeingViewed = this.props.coachId || this.props.managerId || this.props.adminId;
    if (!userBeingViewed) return;

    KpiObservationActions.search.defer({
      userId: userBeingViewed,
      startDate: this.props.startDate,
      endDate: this.props.endDate,
      search: '',
      page: 1,
      count: 1,
      sortDirection: SortDirection.DESC,
    });
    KpiObservationActions.getReminders.defer(userBeingViewed);
  }

  componentDidMount() {
    const {managerId} = this.props;
    setTimeout(() => {
      ExecDashboardActions.getTeamsInAccount.defer();
      TeamActions.fetchTeamDepartments.defer();
      DepartmentFilterActions.fetchAllDepartments.defer();
      FolderActions.fetchFolders.defer();
    });

    if (hasFlag(featureFlags.adminKpis)) {
      this.fetchKpiData();
    }

    const user = AuthStore.getState().user;
    this.getUsers();
    if (managerId) {
      DirectReportsActions.directReports(managerId).then(directReports =>
        this.checkForRoleEdgeCases(directReports, managerId, user)
      );
    }
    // get the tagIds so we can link to some pages in the Inbox tab
    const inboxId = this.props.inboxId;
    inboxId && this.getTags(inboxId);

    ensureReviewMetadata();

    this.updateDashHistoryStack(this.props.location);

    this.maybePrepopCoachingPlanDialog();
  }

  async maybePrepopCoachingPlanDialog() {
    const {location} = this.props;
    const showCoachingDialog = getQueryParam(location, 'showCoachingDialog');

    if (showCoachingDialog) {
      const preloadSkillId = getNumberQueryParam(location, 'preloadSkillId');
      const preloadRepId = getStringQueryParam(location, 'preloadRepId');
      const {coachId} = this.props.params;

      const rep = await UserActions.fetchAll({
        ids: List([preloadRepId]),
      }).then(r => r.get(0));
      const skill = await SkillActions.fetchAllByTeam({team_id: rep.teamId}).then(([skills]) =>
        skills.find(s => s.id === preloadSkillId)
      );

      this.props.onCreatePlan({
        coach: AuthStore.getState().user,
        rep,
        skillName: skill.skill,
        refreshCoachingData: () => {
          const loadForCoach = !!coachId;

          UpcomingReviewsActions.upcomingReviews(
            AuthStore.getState().user.id,
            loadForCoach ? UPCOMING_REVIEW_ROLES.ALL : UPCOMING_REVIEW_ROLES.COACH
          );
        },
      });
    }
  }

  clearSelectedTeamOnManagerChange(newState, thisAdminId, thisManagerId, prevManagerId) {
    if (
      thisAdminId &&
      // the first condition means we had a manager and now don't, and the selected team does not belong
      // to the admin (if there is one), so we wipe the selected team (the reason to keep the team in case
      // of the admin owning it is so that if the admin drills down again she keeps the team selection)
      ((prevManagerId &&
        prevManagerId !== thisManagerId &&
        !this.currentTeamBelongsToManager(thisAdminId)) ||
        // the second condition means we didn't have a manager and now we do, so the team cannot be useful,
        // unless it is an admin drilling down to her own manager dash
        (!prevManagerId && thisManagerId && !this.currentTeamBelongsToManager(thisManagerId)))
    ) {
      // order is important, in the case of having a new manager the state may be set next
      newState.selectedTeam = null;
    }
  }

  setSelectedTeamIfPassedInViaState(newState) {
    const prevTeam = this.state.selectedTeam;
    const newTeam = this.props.location.state?.selectedTeam;
    if (newTeam && newTeam.id !== prevTeam?.id) {
      newState.selectedTeam = newTeam;
    }
  }

  getRequisiteData(thisManagerId, prevManagerId, thisCoachId, prevCoachId, prevProps) {
    if (thisCoachId !== prevCoachId || thisManagerId !== prevManagerId) {
      this.getUsers();
    }
    if (thisManagerId && thisManagerId !== prevManagerId) {
      DirectReportsActions.directReports(thisManagerId);
    }
    const inboxId = this.props.inboxId;
    if (inboxId && inboxId !== prevProps.inboxId) {
      this.getTags(inboxId);
    }
  }

  /**
   * Checks whether we are back to the top-level dash viewer
   * I.e. if the authUser is an admin then are we viewing an admin-dash page, or
   * if the authUser is a manager then are we viewing a manager-dash page?
   * @param pathname the path of the page that we are about to open
   */
  isBackToTheTop(pathname) {
    if (!pathname) return false;
    const authUser = AuthStore.getState().user;
    const dashType = getDashboardType(authUser);
    if (
      dashType === DashboardTypeEnum.exec &&
      pathname.indexOf('/manager/') < 0 &&
      pathname.indexOf('/coach/') < 0
    ) {
      return true;
    }
    return dashType === DashboardTypeEnum.manager && pathname.indexOf('/coach/') < 0;
  }

  updateDashHistoryStack(location) {
    if (!location) return;
    const history = DashboardHistoryStore.getState().history;
    if (location.state && location.state.pathname) {
      const state = location.state;
      if (state && state.pathname) {
        // XXX just being defensive, can remove after testing
        if (!state.action) {
          console.warn('state.pathname is set but state.action is not', state.pathname);
        }
        if (state.action === HISTORY_ACTIONS.PUSH) {
          if (history.isEmpty() || history.last() !== state.pathname) {
            // TODO handle start and end dates that can also be coming from the back button (when going from rep to man)
            DashboardHistoryActions.pushHistory.defer(state.pathname);
          }
        } else if (state.action === HISTORY_ACTIONS.POP) {
          if (!history.isEmpty() && history.last() === state.pathname) {
            // Only pop the one that we want, in case of race condition
            DashboardHistoryActions.popHistory.defer(state.pathname);
          }
        }
      }
    } else if (this.isBackToTheTop(location.pathname)) {
      if (!history.isEmpty()) {
        DashboardHistoryActions.resetHistory.defer();
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.props.selectedDepartments.equals(prevProps.selectedDepartments)) {
      ExecDashboardActions.getTeamsInAccount.defer();
    }

    const {
      coachId: thisCoachId,
      managerId: thisManagerId,
      adminId: thisAdminId,
    } = this.props.params;
    const {coachId: prevCoachId, managerId: prevManagerId} = prevProps.params;
    const newState = {};
    this.clearSelectedTeamOnManagerChange(newState, thisAdminId, thisManagerId, prevManagerId);
    this.setSelectedTeamIfPassedInViaState(newState);
    if (Object.keys(newState).length > 0) {
      this.setState(newState);
    }
    this.updateDashHistoryStack(this.props.location);
    this.getRequisiteData(thisManagerId, prevManagerId, thisCoachId, prevCoachId, prevProps);

    if (thisCoachId !== prevCoachId || thisManagerId !== prevManagerId) {
      return this.fetchKpiData();
    }
  }

  /**
   * manager role w/o any direct reports are special - redirect them appropriately
   *
   * @param directReports
   * @param managerId
   * @param authUser
   */
  checkForRoleEdgeCases(directReports, managerId, authUser) {
    if (authUser.isAdmin) return;
    if (directReports.size === 0) {
      if (authUser.is_coach) {
        const link = buildUrl({coachId: managerId});
        browserHistory.replace(link);
      } else {
        browserHistory.replace(`/metrics/profile?userId=${authUser.id}`);
      }
    }
  }

  refreshCoachingData() {
    this.setState({
      coachingPlansTracker: this.state.coachingPlansTracker + 1,
    });
  }

  currentTeamBelongsToManager(managerId) {
    const team = this.state.selectedTeam;
    if (!team) return false;
    const teamManagerId = team.managerId
      ? team.managerId
      : team.manager
      ? team.manager.userId
      : null;
    return managerId && managerId === teamManagerId;
  }

  getTags(inboxId) {
    TagActions.fetchTags.defer(getFilters({folderId: inboxId}));
  }

  getUsers() {
    const {coachId, managerId, adminId} = this.props;

    // For all dashboards, grab admin, coach and manager if they're missing.
    fetchMissingUsers([managerId, coachId, adminId]).catch(console.error);

    // For admin dashboard, grab all managers and coaches.
    // Manager dashboards get users via direct reports or the widgets themselves.
    // TODO consolidate manager dash fetching away from widgets
    const authUser = AuthStore.getState().user;
    if (authUser && getDashboardType(authUser) === DashboardTypeEnum.exec) {
      UserActions.fetchManagersAndCoaches().catch(console.error);
    }
  }

  onSelectTeam(team, doRedirect = false) {
    const lastTeam = this.state.selectedTeam;
    this.setState({selectedTeam: team}, () => {
      if (
        doRedirect &&
        lastTeam &&
        lastTeam.id !== team.id &&
        (this.props.params.repId || this.props.params.coachId)
      ) {
        // Reps/Coaches can only be on one team, so if that is defined then reload
        return this.redirectForNewTeam(team);
      }
    });
  }

  redirectForNewTeam(team) {
    const users = TeamListStore.getState().users.get(team.id);
    const repId = users?.first()?.id;

    if (repId) {
      return this.redirectToSamePageType(repId);
    }
  }

  async redirectToSamePageType(newRepId) {
    // XXX obviously this ties us to use just one phrase as the page type
    const page = this.props.location.pathname.split('/').pop();
    const users = UserStore.getState().users;
    const repOrCoach = users.has(newRepId)
      ? users.get(newRepId)
      : await UserSource.fetchAll({ids: [newRepId]}).then(u => u.first());

    if (!repOrCoach) console.warn('Cannot find rep for id', newRepId);

    const {managerId, adminId} = this.props.params;
    const link = getCoachRepLink({rep: repOrCoach}, {adminId, managerId}, null, false, page as any);
    browserHistory.push(link);
  }

  /**
   * Render the user whose dashboard we are viewing
   */
  renderAssumedUser() {
    const {rep, coach, manager} = this.props;
    const authUser = AuthStore.getState().user;
    const dashType = getDashboardType(authUser);
    const isAdmin = dashType === DashboardTypeEnum.exec;
    const isManager = dashType === DashboardTypeEnum.manager || isAdmin;
    const isCoach = dashType === DashboardTypeEnum.coach || isManager;

    if (isCoach && rep) return <AssumedUserBadge type="rep" user={rep} />;
    if (isManager && !!coach) return <AssumedUserBadge type="coach" user={coach} />;
    if (isAdmin && !!manager) return <AssumedUserBadge type="manager" user={manager} />;
  }

  pageHas9Box(onSummary) {
    const {coachId, managerId} = this.props;
    const pageRenders9Box = onSummary && !coachId && !!managerId;
    return hasFlag(featureFlags.enhancedManagerDash) && pageRenders9Box;
  }

  render() {
    const {selectedTeam} = this.state;
    const {
      location,
      directReports,
      inboxId,
      tagIdsByName,
      selectedDepartments,
      startDate,
      endDate,
      headerStartDate,
      headerEndDate,
      coachId,
      managerId,
      adminId,
    } = this.props;

    const pageParts = location.pathname.split('/');
    const page = pageParts[pageParts.length - 1];
    const onSummary = page === 'summary';

    const fromPath = this.props.location.pathname;
    return (
      <div className="dashv2-page">
        <div className="sidebar">
          <DashboardBackButton />
          {this.renderAssumedUser()}
          {coachId ? (
            <CoachDashboardSidebar {...this.props} />
          ) : managerId ? (
            <ManagerDashboardSidebar {...this.props} />
          ) : (
            <AdminDashboardSidebar {...this.props} />
          )}
        </div>
        <div className="dashv2-content">
          <div className="content-header">
            <DashboardHeader
              {...this.props}
              fromPath={fromPath}
              onSummary={onSummary}
              selectedTeam={selectedTeam}
              startDate={headerStartDate}
              endDate={headerEndDate}
              isTopicRollup={this.props.isTopicRollup}
              onSelectTeam={this.onSelectTeam}
              onSelectRep={newRepId => this.redirectToSamePageType(newRepId)}
            />
          </div>
          <div className="content-body">
            {this.props.children &&
              React.cloneElement(this.props.children as any, {
                // TODO determine which of these props should be here
                ...this.props,
                startDate,
                endDate,
                fromPath,
                selectedTeam: this.state.selectedTeam,
                directReports,
                inboxId,
                tagIdsByName,
                coachingPlansTracker: this.state.coachingPlansTracker,
                // re-render children any time selected departments change
                key: selectedDepartments.join(''),
                targetUser: this.props.coach || this.props.manager || this.props.admin,
              })}
            {shouldRenderSidebar({adminId, coachId}) && (
              <div className="right-sidebar" id="right-sidebar">
                <AdminDashSearchSidebar
                  refreshCoachingData={() => {
                    loadUpcomingReviewData(managerId, coachId);
                    coachId && refreshCoachingPlansAndVelocities(coachId); // only need to do this for coach dash.
                  }}
                  startDate={this.props.startDate}
                  endDate={this.props.endDate}
                  fromPath={fromPath}
                  adminId={this.props.adminId}
                  managerId={this.props.managerId}
                  coachId={this.props.coachId}
                  hasRenderedDashv2Gif={true}
                  location={this.props.location}
                />
                {!this.pageHas9Box(onSummary) && (
                  <UpcomingCoachingAgenda
                    location={location}
                    coachId={coachId}
                    managerId={managerId}
                    adminId={adminId}
                    refreshCoachingData={this.refreshCoachingData}
                  />
                )}
              </div>
            )}
            <ModalExtrasContainer
              reminderReviewId={getNumberQueryParam(this.props.location, 'reminderReviewId')}
            />
          </div>
        </div>
      </div>
    );
  }

  componentWillUnmount() {
    ManagerDashboardActions.reset.defer();
  }
}

export default withCoachingPlanWizard(
  StatsPage2
) as unknown as React.ComponentType<StatsPage2Props>;
